import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatIqd, fromMillidinars } from '@gc/shared';

import { useSafeBalance } from '@/features/safe/useSafe';
import { ApiError } from '@/lib/api';
import { signedMoney } from './DrawerFigures';
import {
  parseWholeIqd,
  startAdjustmentMd,
  startCashDrawer,
  startCashDrawerBody,
  useBoxesHoldingCash,
  type OpenBoxRow,
} from './useCashDrawer';

/** Where `startCashDrawerSchema` caps the note. */
const NOTE_MAX = 500;

/**
 * The boxes that still hold CASH, listed so the owner can reset them first
 * (owner, 2026-10-12: listed and suggested, never blocking). Error before
 * empty: a failed read never reads as "no box holds cash".
 */
export function BoxesHoldingCash({
  rows,
  error,
  currencySymbol,
}: {
  /** Undefined until the read answers. */
  rows: OpenBoxRow[] | undefined;
  error: string | null;
  currencySymbol: string;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });
  const box = 'mt-3 rounded-control border border-subtle bg-raised px-3 py-2 text-sm';

  if (error) {
    return (
      <p role="alert" data-boxes="error" className={`${box} text-danger`}>
        {error}
      </p>
    );
  }
  if (rows === undefined) {
    return (
      <p data-boxes="loading" className={`${box} text-content-muted`}>
        {t('cashDrawer.start.boxesLoading')}
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p data-boxes="none" className={`${box} text-content-secondary`}>
        {t('cashDrawer.start.boxesNone')}
      </p>
    );
  }
  return (
    <div data-boxes={rows.length} className={box}>
      <p className="font-medium text-content">{t('cashDrawer.start.boxesTitle')}</p>
      <p className="mt-0.5 text-xs text-content-muted">{t('cashDrawer.start.boxesHint')}</p>
      <ul className="mt-2 space-y-1">
        {rows.map((row) => (
          <li key={row.employeeId} className="flex items-baseline justify-between gap-3">
            <span className="truncate text-content">{row.employeeName}</span>
            {/* A box can hold NEGATIVE cash after refunds; it is listed too. */}
            <span
              className={`tabular shrink-0 ${
                row.split.cashMd.startsWith('-') ? 'text-danger' : 'text-content'
              }`}
            >
              {money(row.split.cashMd)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * "Start using the cash drawer" — the owner's one-way switch-over
 * (2026-10-12), one transaction on the server:
 *
 * - the counted safe writes an ADJUSTMENT slip (count − the safe on the
 *   books), because the old figure added customer-balance and FIB money that
 *   were never notes;
 * - the counted drawer opens the first session as its float;
 * - the flag turns on: payments stamp the drawer, box resets stop.
 *
 * Both counts start empty — a figure copied from the screen is no count —
 * and a tick confirms the step cannot be undone.
 */
export function StartCashDrawerDialog({
  currencySymbol,
  onClose,
  onStarted,
  onAlreadyStarted,
}: {
  currencySymbol: string;
  onClose: () => void;
  onStarted: () => void;
  /** Another tab or device started it first; the parent re-reads the drawer. */
  onAlreadyStarted: () => void;
}) {
  const { t } = useTranslation();
  const { data: safe, error: safeError } = useSafeBalance();
  const { rows: boxes, error: boxesError } = useBoxesHoldingCash();
  const [safeRaw, setSafeRaw] = useState('');
  const [floatRaw, setFloatRaw] = useState('');
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  const countedSafeIqd = parseWholeIqd(safeRaw);
  const countedFloatIqd = parseWholeIqd(floatRaw);
  const adjustmentMd =
    countedSafeIqd !== null && safe ? startAdjustmentMd(countedSafeIqd, safe.balanceMd) : null;
  const ready = countedSafeIqd !== null && countedFloatIqd !== null && confirmed;
  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (countedSafeIqd === null || countedFloatIqd === null || !confirmed) return;
    setError(null);
    setSubmitting(true);
    try {
      await startCashDrawer(startCashDrawerBody({ countedSafeIqd, countedFloatIqd, note }));
      onStarted();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'drawer_already_enabled') {
        setError(t('cashDrawer.start.alreadyEnabled'));
        onAlreadyStarted();
      } else {
        setError(caught instanceof ApiError ? caught.message : t('cashDrawer.start.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        role="dialog"
        aria-modal="true"
        aria-label={t('cashDrawer.start.title')}
        className="max-h-full w-full max-w-lg overflow-auto rounded-card border border-subtle bg-ground p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">{t('cashDrawer.start.title')}</h2>
        <p className="mt-2 text-sm leading-relaxed text-content-secondary">
          {t('cashDrawer.start.intro')}
        </p>

        <BoxesHoldingCash rows={boxes} error={boxesError} currencySymbol={currencySymbol} />

        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('cashDrawer.start.safeLabel')}
          </span>
          <div className="flex items-center gap-2">
            <input
              name="countedSafe"
              value={safeRaw}
              onChange={(event) => setSafeRaw(event.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              autoComplete="off"
              className={`${inputClass} tabular`}
            />
            <span className="text-sm text-content-muted">{currencySymbol}</span>
          </div>
          <span className="mt-1 block text-xs text-content-muted">
            {t('cashDrawer.start.safeHint')}
          </span>
        </label>
        {safeError ? (
          <p role="alert" data-safe-balance="error" className="mt-1 text-xs text-danger">
            {safeError}
          </p>
        ) : safe ? (
          <p
            data-safe-balance={safe.balanceMd}
            className="tabular mt-1 text-xs text-content-secondary"
          >
            {t('cashDrawer.start.booksSay', { amount: money(safe.balanceMd) })}
          </p>
        ) : (
          <p className="mt-1 text-xs text-content-muted">{t('cashDrawer.start.booksLoading')}</p>
        )}
        {adjustmentMd !== null ? (
          <p
            data-adjustment={adjustmentMd}
            className="tabular mt-1 text-sm font-medium text-content"
          >
            {t('cashDrawer.start.adjustment', { amount: signedMoney(adjustmentMd, money) })}
          </p>
        ) : null}

        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('cashDrawer.start.floatLabel')}
          </span>
          <div className="flex items-center gap-2">
            <input
              name="countedFloat"
              value={floatRaw}
              onChange={(event) => setFloatRaw(event.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              autoComplete="off"
              className={`${inputClass} tabular`}
            />
            <span className="text-sm text-content-muted">{currencySymbol}</span>
          </div>
          <span className="mt-1 block text-xs text-content-muted">
            {t('cashDrawer.start.floatHint')}
          </span>
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('cashDrawer.start.note')}
          </span>
          <input
            name="note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={NOTE_MAX}
            placeholder={t('cashDrawer.start.notePlaceholder')}
            className={inputClass}
          />
        </label>

        <label className="mt-4 flex items-start gap-2 text-sm text-content">
          <input
            type="checkbox"
            name="confirmed"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            className="mt-0.5"
          />
          <span>{t('cashDrawer.start.confirm')}</span>
        </label>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !ready}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {submitting ? t('common.loading') : t('cashDrawer.start.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
